import { Card } from "@/components/ui/card"
import { DashboardResponse } from "@/models/dashboard/dashboard";
import { formatPrice } from "@/utils/number";
import { cn } from "@/lib/utils"
// import MaterialSymbolsListRounded from '~icons/material-symbols/list-rounded';
// import MdiCurrencyThb from '~icons/mdi/currency-thb';
import RiBillFill from '~icons/ri/bill-fill';

export function SectionCards({
  data,
}: {
  data?: DashboardResponse
}) {
  const formatDecimal = (value: number, digits = 2) =>
    value.toLocaleString("en-US", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })

  const sellTotalPrice = data?.sell?.total_price ?? 0
  const buyTotalPrice = data?.buy?.total_price ?? 0
  const expensesTotal = data?.all?.expenses_total ?? 0
  const sellTotalQuantity = data?.sell?.total_quantity ?? 0
  const buyTotalQuantity = data?.buy?.total_quantity ?? 0
  const profitLossBeforeExpense = sellTotalPrice - buyTotalPrice
  const profitLoss =
    data?.all?.net_profit !== undefined && data?.all?.net_profit !== null
      ? data.all.net_profit
      : profitLossBeforeExpense - expensesTotal
  const profitLossKg = sellTotalQuantity - buyTotalQuantity
  const profitPerKg =
    sellTotalQuantity > 0 ? profitLossBeforeExpense / sellTotalQuantity : 0
  const isProfit = profitLoss >= 0
  const isProfitKg = profitLossKg >= 0
  const isProfitPerKg = profitPerKg >= 0
  const absProfitLoss = Math.abs(profitLoss)
  const absProfitLossKg = Math.abs(profitLossKg)
  const absProfitPerKg = Math.abs(profitPerKg)

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 w-full gap-6 px-0 mt-4">
      {/* Total Bills Card */}
      <Card className="flex flex-col p-6 w-full shadow-sm border rounded-[2rem]">
        <div className="flex justify-between items-start mb-6">
          <div className="bg-secondary text-primary p-2 rounded-xl">
            <RiBillFill className="size-6" />
          </div>
          <div className="bg-secondary/80 text-primary text-xs font-bold px-3 py-1 rounded-full">สรุปยอดบิล</div>
        </div>
        <div className="mb-2">
          <div className="text-gray-500 font-semibold mb-1">บิลทั้งหมด</div>
          <div className="flex items-baseline gap-1">
            <h2 className="text-4xl font-extrabold text-slate-900 tracking-tight">{formatPrice(data?.all?.total_quantity ?? 0)}</h2>
            <span className="text-gray-500 font-medium text-lg">กิโลกรัม</span>
          </div>
        </div>
        <div className="grid grid-cols-2 mt-6 pt-4 border-t border-gray-100">
          <div>
            <div className="text-gray-400 text-xs font-medium mb-1">จำนวนรายการ</div>
            <div className="text-lg font-bold text-slate-900">{formatPrice(data?.all?.total ?? 0)}</div>
          </div>
          <div>
            <div className="text-gray-400 text-xs font-medium mb-1">มูลค่า (บาท)</div>
            <div className="text-lg font-bold text-slate-900">{formatPrice(data?.all?.total_price ?? 0)}</div>
          </div>
        </div>
      </Card>

      {/* Outgoing Card */}
      <Card className="flex flex-col p-6 w-full shadow-sm border rounded-[2rem]">
        <div className="flex justify-between items-start mb-6">
          <div className="bg-red-50 text-red-500 p-2 rounded-xl">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 7V21a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><line x1="16" y1="3" x2="16" y2="21"/><line x1="8" y1="3" x2="8" y2="21"/><path d="M12 8l4 4-4 4"/></svg> {/* Using standard lucide icons if possible, or just mock it */}
          </div>
          <div className="bg-red-100 text-red-700 text-xs font-bold px-3 py-1 rounded-full">ยอดขายออก</div>
        </div>
        <div className="mb-2">
          <div className="text-gray-500 font-semibold mb-1">ขายออก</div>
          <div className="flex items-baseline gap-1">
            <h2 className="text-4xl font-extrabold text-slate-900 tracking-tight">{formatPrice(data?.sell?.total_quantity ?? 0)}</h2>
            <span className="text-gray-500 font-medium text-lg">กิโลกรัม</span>
          </div>
        </div>
        <div className="grid grid-cols-2 mt-6 pt-4 border-t border-gray-100">
          <div>
            <div className="text-gray-400 text-xs font-medium mb-1">จำนวนรายการ</div>
            <div className="text-lg font-bold text-red-600">{formatPrice(data?.sell?.total ?? 0)}</div>
          </div>
          <div>
            <div className="text-gray-400 text-xs font-medium mb-1">มูลค่า (บาท)</div>
            <div className="text-lg font-bold text-slate-900">{formatPrice(data?.sell?.total_price ?? 0)}</div>
          </div>
        </div>
      </Card>

      {/* Incoming Card */}
      <Card className="flex flex-col p-6 w-full shadow-sm border rounded-[2rem]">
        <div className="flex justify-between items-start mb-6">
          <div className="bg-secondary text-primary p-2 rounded-xl">
            <div className="h-6 w-6 relative">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M22 12l-2.29-2.29-4.88 4.88-4-4L3.41 18 2 16.59l8-8 4 4 6.3-6.29L18 4h6z"/></svg> 
            </div>
          </div>
          <div className="bg-secondary/80 text-primary text-xs font-bold px-3 py-1 rounded-full">ยอดซื้อเข้า</div>
        </div>
        <div className="mb-2">
          <div className="text-gray-500 font-semibold mb-1">ซื้อเข้า</div>
          <div className="flex items-baseline gap-1">
            <h2 className="text-4xl font-extrabold text-slate-900 tracking-tight">{formatPrice(data?.buy?.total_quantity ?? 0)}</h2>
            <span className="text-gray-500 font-medium text-lg">กิโลกรัม</span>
          </div>
        </div>
        <div className="grid grid-cols-2 mt-6 pt-4 border-t border-gray-100">
          <div>
            <div className="text-gray-400 text-xs font-medium mb-1">จำนวนรายการ</div>
            <div className="text-lg font-bold text-primary">{formatPrice(data?.buy?.total ?? 0)}</div>
          </div>
          <div>
            <div className="text-gray-400 text-xs font-medium mb-1">มูลค่า (บาท)</div>
            <div className="text-lg font-bold text-slate-900">{formatPrice(data?.buy?.total_price ?? 0)}</div>
          </div>
        </div>
      </Card>

      {/* Profit/Loss Card */}
      <Card className="flex flex-col p-6 w-full shadow-sm border rounded-[2rem]">
        <div className="flex justify-between items-start mb-6">
          <div
            className={cn(
              "p-2 rounded-xl",
              isProfit ? "bg-secondary text-primary" : "bg-rose-50 text-rose-500"
            )}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 3v18h18" />
              <path d="M7 14l4-4 3 3 5-5" />
            </svg>
          </div>
          <div
            className={cn(
              "text-xs font-bold px-3 py-1 rounded-full",
              isProfit ? "bg-secondary/80 text-primary" : "bg-rose-100 text-rose-700"
            )}
          >
            {isProfit ? "กำไร" : "ขาดทุน"}
          </div>
        </div>
        <div className="mb-2">
          <div className="text-gray-500 font-semibold mb-1">กำไรสุทธิ (หลังหักค่าใช้จ่าย)</div>
          <div className="flex items-baseline gap-1">
            <h2
              className={cn(
                "text-4xl font-extrabold tracking-tight",
                isProfit ? "text-primary" : "text-rose-600"
              )}
            >
              {isProfit ? "+" : "-"}
              {formatPrice(absProfitLoss)}
            </h2>
            <span className="text-gray-500 font-medium text-lg">บาท</span>
          </div>
        </div>
        <div className="grid grid-cols-2 mt-6 pt-4 border-t border-gray-100 gap-y-3">
          <div>
            <div className="text-gray-400 text-xs font-medium mb-1">ยอดขาย (บาท)</div>
            <div className="text-lg font-bold text-slate-900">{formatPrice(sellTotalPrice)}</div>
          </div>
          <div>
            <div className="text-gray-400 text-xs font-medium mb-1">ยอดซื้อ (บาท)</div>
            <div className="text-lg font-bold text-slate-900">{formatPrice(buyTotalPrice)}</div>
          </div>
          <div>
            <div className="text-gray-400 text-xs font-medium mb-1">ปริมาณสุทธิ (กก.)</div>
            <div
              className={cn(
                "text-lg font-bold",
                isProfitKg ? "text-primary" : "text-rose-600"
              )}
            >
              {isProfitKg ? "+" : "-"}
              {formatDecimal(absProfitLossKg)}
            </div>
          </div>
          <div>
            <div className="text-gray-400 text-xs font-medium mb-1">ค่าใช้จ่าย (บาท)</div>
            <div className="text-lg font-bold text-t-warning">{formatPrice(expensesTotal)}</div>
          </div>
          <div>
            <div className="text-gray-400 text-xs font-medium mb-1">กำไรหลังหักค่าใช้จ่าย (บาท)</div>
            <div
              className={cn(
                "text-lg font-bold",
                profitLoss >= 0 ? "text-primary" : "text-rose-600"
              )}
            >
              {profitLoss >= 0 ? "+" : "-"}
              {formatPrice(Math.abs(profitLoss))}
            </div>
          </div>
          <div>
            <div className="text-gray-400 text-xs font-medium mb-1">กำไรเฉลี่ยต่อกก. (บาท/กก.)</div>
            <div
              className={cn(
                "text-lg font-bold",
                isProfitPerKg ? "text-primary" : "text-rose-600"
              )}
            >
              {sellTotalQuantity > 0 ? (
                <>
                  {isProfitPerKg ? "+" : "-"}
                  {formatDecimal(absProfitPerKg)}
                </>
              ) : (
                "-"
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}
